import { api } from '@/lib/axios';
import { type PdfMode, renderPdf } from '@/lib/pdf';

export type Granularite = 'jour' | 'semaine' | 'mois';
export type SensLocation = 'sortante' | 'entrante' | 'toutes';

export interface DocumentLocation {
  document_id: string;
  reference: string | null;
  sens: 'SORTANTE' | 'ENTRANTE';
  statut: string;
  engin_id: string;
  engin_libelle: string;
  engin_marque: string | null;
  engin_modele: string | null;
  engin_categorie: string | null;
  engin_immatriculation: string | null;
  tiers_libelle: string;
  chantier_libelle: string | null;
  tarif_jour: string;
  tarif_heure: string;
  modifiable: boolean;
}

export interface LigneEtat {
  date: string;
  date_fin: string | null;   // borne haute quand la ligne cumule une période
  agrege: boolean;           // true = ligne cumulée (hebdo / mensuel), pas une journée
  ligne_id: string;
  sens: 'SORTANTE' | 'ENTRANTE';
  document_id: string;
  document_reference: string | null;
  statut: string;
  engin_id: string;
  engin_libelle: string;
  engin_marque: string | null;
  engin_modele: string | null;
  engin_categorie: string | null;
  tiers_libelle: string;
  chantier_libelle: string | null;
  chauffeur: string | null;
  jour_repos: boolean;       // journée travaillée un jour de repos (forçage assumé)
  journees: string;
  heures_sup: string;
  tarif_jour: string;
  tarif_heure: string;
  gasoil_litres: string;
  gasoil_montant: string;
  mode_gasoil: string | null;
  brut: string;
  deduction_gasoil: string;
  net: string;
  cout_impute: string;       // gasoil à ma charge (sortante, mode « inclus »)
  marge: string | null;      // sortante uniquement : net − coûts imputés
  cumul: string;
}
export interface SousTotalEtat {
  journees: string; heures_sup: string; gasoil_litres: string; gasoil_montant: string;
  brut: string; deduction_gasoil: string; net: string;
  cout_impute: string; marge: string; avances: string; solde: string;
}
export interface GroupeEtat {
  periode: string;
  libelle: string;
  lignes: LigneEtat[];
  sous_total: SousTotalEtat;
  sous_total_sortante: SousTotalEtat;
  sous_total_entrante: SousTotalEtat;
}
/**
 * Deux flux OPPOSÉS, jamais additionnés : `sortante` est ce que les clients me doivent
 * (à encaisser), `entrante` ce que je dois aux loueurs (à payer). `position` est le seul
 * chiffre unique — une position de trésorerie, pas un montant de facture.
 */
export interface EtatLocation {
  granularite: Granularite;
  sens: SensLocation;
  date_from: string | null;
  date_to: string | null;
  filtre_libelle: string;
  jour_repos: number;        // 0 = lundi … 6 = dimanche (convention Python)
  documents: DocumentLocation[];
  groupes: GroupeEtat[];
  sortante: SousTotalEtat;   // À ENCAISSER
  entrante: SousTotalEtat;   // À PAYER
  position: string;          // à encaisser − à payer (trésorerie)
  cout_entretien: string;    // indicatif, non déduit de la marge
  total: SousTotalEtat;      // volumétrie (jours, heures, litres)
  montants_masques: boolean; // profil sans le droit « tarifs » : montants neutralisés (0) par le serveur
}

export interface EtatFiltres {
  granularite?: Granularite;
  sens?: SensLocation;
  engin_id?: string;
  tiers_id?: string;
  chantier_id?: string;
  date_from?: string;
  date_to?: string;
}

const clean = (f: object): Record<string, unknown> =>
  Object.fromEntries(Object.entries(f).filter(([, v]) => v !== undefined && v !== ''));

/** État de location agrégé par jour / semaine / mois (lecture seule). */
export async function getEtatLocation(filtres: EtatFiltres): Promise<EtatLocation> {
  const { data } = await api.get<EtatLocation>('/etats-location', { params: clean(filtres) });
  return data;
}

/** Ouvre / télécharge l'état imprimable A4 (PDF : en-tête entreprise, tableau, récap net). */
export async function openEtatLocationPdf(filtres: EtatFiltres, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf('/etats-location/pdf', clean(filtres), mode);
}

/**
 * Périmètre d'une fiche de pointage mensuelle : UNE machine, TOUS les engins d'un loueur, ou les
 * machines qui correspondent aux filtres des états (sens / client-loueur / chantier).
 */
export interface FicheFiltres {
  annee: number;
  mois: number;                // 1 → 12
  engin_id?: string;
  loueur_id?: string;
  sens?: SensLocation;
  tiers_id?: string;
  chantier_id?: string;
}
export type FichePerimetre = Omit<FicheFiltres, 'annee' | 'mois'>;

/** Ouvre / télécharge la fiche de pointage mensuelle : une page A4 par machine, tous les jours du mois. */
export async function openFicheMensuellePdf(filtres: FicheFiltres, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf('/etats-location/fiche-mensuelle/pdf', clean(filtres), mode);
}

/** Pointe (ou efface) UNE journée directement depuis la grille — upsert idempotent par date.
 *  `sens` choisit le module : location sortante (revenu) ou décompte entrant (loueur). */
export async function pointerJour(
  sens: 'SORTANTE' | 'ENTRANTE',
  documentId: string,
  payload: {
    date: string; journees: string; heures_sup?: string; gasoil_montant?: string;
    /** Pointage horaire (location ENTRANTE) : journées et h. sup sont alors calculées par le serveur. */
    heure_debut?: string; heure_fin?: string; motif_perte?: string;
    /** Gasoil du jour (location ENTRANTE) : litres, et montant calculé par le serveur s'il est omis. */
    gasoil_litres?: string;
  },
): Promise<void> {
  const base = sens === 'SORTANTE' ? '/locations-sortantes' : '/decomptes-location';
  // Côté entrant, le gasoil avancé porte un autre nom de champ.
  const body = sens === 'SORTANTE'
    ? payload
    : { date: payload.date, journees: payload.journees, heures_sup: payload.heures_sup,
        gasoil_avance: payload.gasoil_montant, gasoil_litres: payload.gasoil_litres, heure_debut: payload.heure_debut,
        heure_fin: payload.heure_fin, motif_perte: payload.motif_perte };
  await api.put(`${base}/${documentId}/pointage`, body);
}
